/**
 * Glorify AI backend API client (glorify_ai_axum, default port 8020).
 *
 * Resolves base URL from env in this order:
 *   1. VITE_DASHBOARD_GLORIFYAI_API_BASE_URL — explicit full URL or path.
 *      Trailing slashes stripped. Use this for remote hosts or nginx proxying.
 *   2. VITE_DASHBOARD_GLORIFYAI_API_HOST (default: localhost) +
 *      VITE_DASHBOARD_GLORIFYAI_API_PORT (default: 8020).
 *
 * This module is the SINGLE SOURCE OF TRUTH for GLORIFY_API_BASE_URL —
 * dashboard_login.tsx imports it from here rather than duplicating resolution.
 *
 * All requests include `credentials: 'include'` so the HttpOnly `auth_token`
 * cookie is sent on every call. The backend's CORS already allows
 * http://localhost:5173 with credentials.
 */

const resolveGlorifyApiBaseUrl = (): string => {
  const explicit = import.meta.env.VITE_DASHBOARD_GLORIFYAI_API_BASE_URL as string | undefined
  if (explicit) return explicit.replace(/\/+$/, '')

  const host = (import.meta.env.VITE_DASHBOARD_GLORIFYAI_API_HOST as string | undefined) ?? 'localhost'
  const port = (import.meta.env.VITE_DASHBOARD_GLORIFYAI_API_PORT as string | undefined) ?? '8020'
  return `http://${host}:${port}`
}

export const GLORIFY_API_BASE_URL = resolveGlorifyApiBaseUrl()

// ─── Image URL helpers ────────────────────────────────────────────────────────
// These are plain functions (not class methods) so <img src={...}> can use them
// directly without a client instance. They are the single seam to update if
// images move to S3/CDN — change only here, and all consumers follow.

/**
 * URL for a publicly accessible image (no auth required).
 * Returns 200 image bytes if the image is marked public, 404 otherwise.
 */
export function publicImageUrl(fileName: string): string {
  return `${GLORIFY_API_BASE_URL}/images/public/${encodeURIComponent(fileName)}`
}

/**
 * URL for an authenticated image fetch (cookie-authenticated).
 * The HttpOnly auth_token cookie is sent automatically by <img> tags because
 * localhost:5173 → localhost:8020 is same-site (SameSite=Lax permits it).
 * Returns 200 for owner/admin, 403/404 otherwise.
 */
export function imageFileUrl(imageId: number): string {
  return `${GLORIFY_API_BASE_URL}/images/file/${imageId}`
}

// ─── Response shapes ─────────────────────────────────────────────────────────

export interface SessionUser {
  user_id: number
  user_email: string
  user_is_approved: boolean
  user_role: 'admin' | 'user' | 'partner'
  user_created_at: string
  display_name?: string
  avatar_url?: string
}

export interface ImageRecord {
  image_id: number
  image_file_name: string
  image_file_type: string
  image_file_size: number
  user_id: number
  /** 0 pending · 1 processing · 2 completed · 3 failed */
  image_status_id: number
  is_public: boolean
}

/**
 * Returned by PUT /images/{image_id}/visibility.
 * Shape is identical to ImageRecord (updated record with is_public reflected).
 */
export type SetVisibilityResponse = ImageRecord

export interface ImagesListResponse {
  images: ImageRecord[]
}

export interface UploadImageResponse {
  message: string
  image_id: number
  filename: string
  size: number
}

export interface AudioRecord {
  audio_id: number
  audio_file_name: string
  audio_file_type: string
  audio_file_size: number
  user_id: number
  /** 0 pending · 1 processing · 2 completed · 3 failed */
  audio_status_id: number
}

export interface AudiosListResponse {
  audios: AudioRecord[]
}

export interface UploadAudioResponse {
  message: string
  audio_id: number
  filename: string
  size: number
}

export interface UsersListResponse {
  users: SessionUser[]
}

// ─── Markets (FMP via glorify_ai_axum) ────────────────────────────────────────
// Every markets response carries `as_of` (ISO timestamp of the underlying FMP
// fetch) and `stale` (true when the backend served a cached copy because FMP
// was unreachable). Pages should show a small warning chip when stale.

export interface MarketIndexQuote {
  symbol: string
  name: string
  price: number
  change: number
  change_percentage: number
}

export interface MarketMover {
  symbol: string
  name: string
  price: number
  change: number
  changes_percentage: number
  exchange: string
}

export interface MarketsOverviewResponse {
  indexes: MarketIndexQuote[]
  gainers: MarketMover[]
  losers: MarketMover[]
  most_actives: MarketMover[]
  as_of: string
  stale: boolean
}

export interface SectorPerformance {
  sector: string
  exchange: string
  average_change: number
}

export interface MarketsSectorsResponse {
  date: string
  sectors: SectorPerformance[]
  as_of: string
  stale: boolean
}

export interface IndustryPerformance {
  industry: string
  exchange: string
  average_change: number
}

export interface MarketsIndustriesResponse {
  date: string
  industries: IndustryPerformance[]
  as_of: string
  stale: boolean
}

export interface SectorHistoryPoint {
  date: string
  average_change: number
}

export interface MarketsSectorHistoryResponse {
  sector: string
  points: SectorHistoryPoint[]
  as_of: string
  stale: boolean
}

export interface IndustryHistoryPoint {
  date: string
  average_change: number
}

export interface MarketsIndustryHistoryResponse {
  industry: string
  points: IndustryHistoryPoint[]
  as_of: string
  stale: boolean
}

export interface MarketSearchResult {
  symbol: string
  name: string
  currency: string
  /** Full exchange name, e.g. "NASDAQ Global Select". */
  exchange_full_name: string
  exchange: string
}

export interface MarketsSearchResponse {
  results: MarketSearchResult[]
  as_of: string
  stale: boolean
}

export interface MarketQuoteResponse {
  symbol: string
  name: string
  price: number
  change: number
  change_percentage: number
  volume: number
  day_low: number
  day_high: number
  year_high: number
  year_low: number
  market_cap: number
  open: number
  previous_close: number
  price_avg_50: number
  price_avg_200: number
  exchange: string
  timestamp: number
  as_of: string
  stale: boolean
}

export interface PriceHistoryPoint {
  date: string
  open: number
  high: number
  low: number
  close: number
  volume: number
  change: number
  change_percent: number
  /** Volume-weighted average price for the trading day. */
  vwap: number
}

export interface MarketsHistoryResponse {
  symbol: string
  points: PriceHistoryPoint[]
  as_of: string
  stale: boolean
}

// ─── Invoicing (clients + invoices via glorify_ai_axum) ────────────────────────
// All money/decimal fields (amounts, quantity, unit_price, default_gst_rate)
// are JSON STRINGS on the wire (e.g. "123.45"), never numbers — this avoids
// float-precision drift for accounting values. Format them for display with
// formatAud() in invoicingFormat.ts; send them back to the API as strings too.
// Dates are "YYYY-MM-DD"; timestamps are RFC3339 strings.
//
// Status IDs: 0=draft, 1=sent, 2=paid, 3=overdue, 4=void. See
// invoicingFormat.ts for the id<->label/chip-class/legal-transition maps.

export interface Client {
  client_id: number
  client_name: string
  client_email: string | null
  client_phone: string | null
  client_abn: string | null
  billing_address_line1: string | null
  billing_address_line2: string | null
  billing_city: string | null
  billing_state: string | null
  billing_postcode: string | null
  billing_country: string
  notes: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface ClientsListResponse {
  clients: Client[]
}

export interface CreateClientRequest {
  client_name: string
  client_email?: string
  client_phone?: string
  client_abn?: string
  billing_address_line1?: string
  billing_address_line2?: string
  billing_city?: string
  billing_state?: string
  billing_postcode?: string
  billing_country?: string
  notes?: string
}

/** All fields optional, including `is_active` (used for the deactivate toggle). */
export type UpdateClientRequest = Partial<CreateClientRequest> & { is_active?: boolean }

export interface InvoiceLineItem {
  line_item_id: number
  invoice_id: number
  description: string
  quantity: string
  unit_price: string
  gst_applicable: boolean
  line_total: string
  sort_order: number
}

export interface InvoiceSummary {
  invoice_id: number
  invoice_number: string
  client_id: number
  client_name: string
  invoice_status_id: number
  invoice_status_name: string
  issue_date: string
  due_date: string
  subtotal_amount: string
  gst_amount: string
  total_amount: string
  amount_paid: string
  paid_at: string | null
  created_at: string
}

export interface InvoicesListResponse {
  invoices: InvoiceSummary[]
}

export interface InvoiceDetail {
  invoice_id: number
  invoice_number: string
  invoice_status_id: number
  invoice_status_name: string
  issue_date: string
  due_date: string
  subtotal_amount: string
  gst_amount: string
  total_amount: string
  amount_paid: string
  paid_at: string | null
  notes: string | null
  created_at: string
  updated_at: string
  client: Client
  line_items: InvoiceLineItem[]
}

export interface CreateLineItem {
  description: string
  quantity?: string
  unit_price: string
  gst_applicable?: boolean
  sort_order?: number
}

export interface CreateInvoiceRequest {
  client_id: number
  issue_date?: string
  due_date?: string
  notes?: string
  line_items?: CreateLineItem[]
}

/** PUT /admin/invoices/{id} — 400 unless the invoice is still draft. */
export interface UpdateInvoiceHeaderRequest {
  client_id?: number
  issue_date?: string
  due_date?: string
  notes?: string
}

export interface InvoiceSettings {
  business_name: string
  business_abn: string
  invoice_prefix: string
  next_invoice_sequence: number
  sequence_padding: number
  default_gst_rate: string
  business_address_line1: string | null
  business_address_line2: string | null
  business_city: string | null
  business_state: string | null
  business_postcode: string | null
  business_country: string
  business_email: string | null
  business_phone: string | null
  bank_account_name: string | null
  bank_bsb: string | null
  bank_account_number: string | null
  payment_terms_days: number
  updated_at: string
}

export type UpdateInvoiceSettingsRequest = Partial<Omit<InvoiceSettings, 'updated_at'>>

export interface DeleteInvoiceResponse {
  deleted: true
}

// ─── Typed error ──────────────────────────────────────────────────────────────

/**
 * Thrown by GlorifyApiClient when the server returns a non-2xx response.
 * Check `status` to distinguish 401 (not authenticated) from 403 (not approved
 * or not an admin) from other errors.
 */
export class GlorifyApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'GlorifyApiError'
    this.status = status
  }
}

// ─── Client ──────────────────────────────────────────────────────────────────

export class GlorifyApiClient {
  private readonly baseUrl: string

  constructor(baseUrl: string = GLORIFY_API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`

    // credentials: 'include' overrides whatever options might have had.
    // Headers from options (e.g. Content-Type: application/json for JSON bodies)
    // are preserved via the spread. For FormData uploads, callers omit
    // Content-Type so the browser can set the multipart boundary itself.
    const response = await fetch(url, { ...options, credentials: 'include' })

    if (!response.ok) {
      let message = response.statusText
      try {
        const body = (await response.json()) as { message?: string; error?: string }
        message = body.message ?? body.error ?? message
      } catch {
        // Body was not JSON — keep statusText
      }
      throw new GlorifyApiError(response.status, message)
    }

    if (response.status === 204) {
      return undefined as T
    }

    return response.json() as Promise<T>
  }

  /** GET /users/get_session — 401 if not logged in, 403 if not approved. */
  async getSession(): Promise<SessionUser> {
    return this.request<SessionUser>('/users/get_session')
  }

  /** POST /auth/logout — clears the HttpOnly auth_token cookie server-side. */
  async logout(): Promise<void> {
    return this.request<void>('/auth/logout', { method: 'POST' })
  }

  /** GET /images/list — up to 100 rows, metadata only (no file content). */
  async listImages(): Promise<ImagesListResponse> {
    return this.request<ImagesListResponse>('/images/list')
  }

  /**
   * POST /images/upload — multipart/form-data, field name `file`.
   * Pass the raw File — do NOT set Content-Type manually; the browser sets it
   * with the correct multipart boundary.
   */
  async uploadImage(file: File): Promise<UploadImageResponse> {
    const formData = new FormData()
    formData.append('file', file)
    return this.request<UploadImageResponse>('/images/upload', {
      method: 'POST',
      body: formData,
    })
  }

  /** GET /audios/list — up to 100 rows, metadata only. */
  async listAudios(): Promise<AudiosListResponse> {
    return this.request<AudiosListResponse>('/audios/list')
  }

  /**
   * POST /audios/upload — multipart/form-data, field name `file`.
   * Do NOT set Content-Type manually.
   */
  async uploadAudio(file: File): Promise<UploadAudioResponse> {
    const formData = new FormData()
    formData.append('file', file)
    return this.request<UploadAudioResponse>('/audios/upload', {
      method: 'POST',
      body: formData,
    })
  }

  /** GET /admin/users?approved=<bool> — 403 if caller is not admin. */
  async adminListUsers(approved?: boolean): Promise<UsersListResponse> {
    const qs = approved !== undefined ? `?approved=${String(approved)}` : ''
    return this.request<UsersListResponse>(`/admin/users${qs}`)
  }

  /**
   * PUT /admin/users/{user_id}/approval
   * body: { "approved": bool }
   * Returns the updated user object; 404 if unknown user.
   */
  async adminSetApproval(userId: number, approved: boolean): Promise<SessionUser> {
    return this.request<SessionUser>(`/admin/users/${userId}/approval`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approved }),
    })
  }

  /**
   * PUT /admin/users/{user_id}/role
   * body: { "role": "admin"|"user"|"partner" }
   * Returns the updated user; 400 on invalid role or self-demotion.
   */
  async adminSetRole(userId: number, role: 'admin' | 'user' | 'partner'): Promise<SessionUser> {
    return this.request<SessionUser>(`/admin/users/${userId}/role`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    })
  }

  /**
   * PUT /images/{image_id}/visibility
   * body: { "is_public": boolean }
   * Returns the updated image record; 403 if not owner/admin, 404 if unknown.
   */
  async setImageVisibility(imageId: number, isPublic: boolean): Promise<SetVisibilityResponse> {
    return this.request<SetVisibilityResponse>(`/images/${imageId}/visibility`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_public: isPublic }),
    })
  }

  // ─── Markets (FMP via glorify_ai_axum) ───────────────────────────────────

  /** GET /markets/overview — indexes, gainers, losers, most active. */
  async marketsOverview(): Promise<MarketsOverviewResponse> {
    return this.request<MarketsOverviewResponse>('/markets/overview')
  }

  /** GET /markets/sectors?date=YYYY-MM-DD — sector average change; date optional. */
  async marketsSectors(date?: string): Promise<MarketsSectorsResponse> {
    const qs = date ? `?date=${encodeURIComponent(date)}` : ''
    return this.request<MarketsSectorsResponse>(`/markets/sectors${qs}`)
  }

  /** GET /markets/industries?date=YYYY-MM-DD — industry average change; date optional (defaults to latest trading day). */
  async marketsIndustries(date?: string): Promise<MarketsIndustriesResponse> {
    const qs = date ? `?date=${encodeURIComponent(date)}` : ''
    return this.request<MarketsIndustriesResponse>(`/markets/industries${qs}`)
  }

  /** GET /markets/sectors/history?sector=&from=&to= — trailing sector performance. */
  async marketsSectorHistory(
    sector: string,
    from?: string,
    to?: string
  ): Promise<MarketsSectorHistoryResponse> {
    const params = new URLSearchParams({ sector })
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    return this.request<MarketsSectorHistoryResponse>(`/markets/sectors/history?${params.toString()}`)
  }

  /** GET /markets/industries/history?industry=&from=&to= — trailing industry performance. */
  async marketsIndustryHistory(
    industry: string,
    from?: string,
    to?: string
  ): Promise<MarketsIndustryHistoryResponse> {
    const params = new URLSearchParams({ industry })
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    return this.request<MarketsIndustryHistoryResponse>(`/markets/industries/history?${params.toString()}`)
  }

  /** GET /markets/search?query= — symbol/name lookup for the stock search box. */
  async marketsSearch(query: string): Promise<MarketsSearchResponse> {
    return this.request<MarketsSearchResponse>(`/markets/search?query=${encodeURIComponent(query)}`)
  }

  /** GET /markets/quote/{symbol} — 404 if unknown symbol. */
  async marketsQuote(symbol: string): Promise<MarketQuoteResponse> {
    return this.request<MarketQuoteResponse>(`/markets/quote/${encodeURIComponent(symbol)}`)
  }

  /** GET /markets/history/{symbol}?from=&to= — default range is trailing 1y. */
  async marketsHistory(symbol: string, from?: string, to?: string): Promise<MarketsHistoryResponse> {
    const params = new URLSearchParams()
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.request<MarketsHistoryResponse>(`/markets/history/${encodeURIComponent(symbol)}${qs}`)
  }

  // ─── Invoicing — Clients ──────────────────────────────────────────────────

  /** GET /admin/clients?is_active=true|false — omit filter for all clients. */
  async listClients(isActive?: boolean): Promise<ClientsListResponse> {
    const qs = isActive !== undefined ? `?is_active=${String(isActive)}` : ''
    return this.request<ClientsListResponse>(`/admin/clients${qs}`)
  }

  /** POST /admin/clients — only `client_name` is required. */
  async createClient(payload: CreateClientRequest): Promise<Client> {
    return this.request<Client>('/admin/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  }

  /** GET /admin/clients/{id}. */
  async getClient(clientId: number): Promise<Client> {
    return this.request<Client>(`/admin/clients/${clientId}`)
  }

  /** PUT /admin/clients/{id} — all fields optional, including `is_active`. */
  async updateClient(clientId: number, payload: UpdateClientRequest): Promise<Client> {
    return this.request<Client>(`/admin/clients/${clientId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  }

  // ─── Invoicing — Invoices ─────────────────────────────────────────────────

  /** GET /admin/invoices?invoice_status_id=&client_id= — both filters optional. */
  async listInvoices(filters?: { invoiceStatusId?: number; clientId?: number }): Promise<InvoicesListResponse> {
    const params = new URLSearchParams()
    if (filters?.invoiceStatusId !== undefined) params.set('invoice_status_id', String(filters.invoiceStatusId))
    if (filters?.clientId !== undefined) params.set('client_id', String(filters.clientId))
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.request<InvoicesListResponse>(`/admin/invoices${qs}`)
  }

  /** GET /admin/invoices/{id} — full detail incl. nested client + line items. */
  async getInvoice(invoiceId: number): Promise<InvoiceDetail> {
    return this.request<InvoiceDetail>(`/admin/invoices/${invoiceId}`)
  }

  /** POST /admin/invoices — invoice number is allocated server-side. */
  async createInvoice(payload: CreateInvoiceRequest): Promise<InvoiceDetail> {
    return this.request<InvoiceDetail>('/admin/invoices', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  }

  /** PUT /admin/invoices/{id} — 400 unless the invoice is still draft. */
  async updateInvoiceHeader(invoiceId: number, payload: UpdateInvoiceHeaderRequest): Promise<InvoiceDetail> {
    return this.request<InvoiceDetail>(`/admin/invoices/${invoiceId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  }

  /**
   * PUT /admin/invoices/{id}/status — only the legal next state(s) should be
   * offered by the caller (see legalNextStatuses() in invoicingFormat.ts).
   * Setting `paid` auto-fills `paid_at`/`amount_paid` server-side.
   */
  async updateInvoiceStatus(invoiceId: number, invoiceStatusId: number): Promise<InvoiceDetail> {
    return this.request<InvoiceDetail>(`/admin/invoices/${invoiceId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoice_status_id: invoiceStatusId }),
    })
  }

  /** DELETE /admin/invoices/{id} — draft only; hard delete, cascades line items. */
  async deleteInvoice(invoiceId: number): Promise<DeleteInvoiceResponse> {
    return this.request<DeleteInvoiceResponse>(`/admin/invoices/${invoiceId}`, {
      method: 'DELETE',
    })
  }

  /** POST /admin/invoices/{id}/line-items — draft only; returns the whole updated invoice. */
  async addLineItem(invoiceId: number, payload: CreateLineItem): Promise<InvoiceDetail> {
    return this.request<InvoiceDetail>(`/admin/invoices/${invoiceId}/line-items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  }

  /** PUT /admin/invoices/{id}/line-items/{line_item_id} — all fields optional, draft only. */
  async updateLineItem(
    invoiceId: number,
    lineItemId: number,
    payload: Partial<CreateLineItem>
  ): Promise<InvoiceDetail> {
    return this.request<InvoiceDetail>(`/admin/invoices/${invoiceId}/line-items/${lineItemId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  }

  /** DELETE /admin/invoices/{id}/line-items/{line_item_id} — draft only. */
  async deleteLineItem(invoiceId: number, lineItemId: number): Promise<InvoiceDetail> {
    return this.request<InvoiceDetail>(`/admin/invoices/${invoiceId}/line-items/${lineItemId}`, {
      method: 'DELETE',
    })
  }

  /** GET /admin/invoices/settings — singleton row (ABN, prefix, GST rate, bank details). */
  async getInvoiceSettings(): Promise<InvoiceSettings> {
    return this.request<InvoiceSettings>('/admin/invoices/settings')
  }

  /** PUT /admin/invoices/settings — partial update. */
  async updateInvoiceSettings(payload: UpdateInvoiceSettingsRequest): Promise<InvoiceSettings> {
    return this.request<InvoiceSettings>('/admin/invoices/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  }

  /**
   * GET /admin/invoices/{id}/pdf — JWT-cookie protected (application/pdf, not
   * JSON), so this bypasses `request()` and instead fetches a blob, wraps it
   * in an object URL, and triggers a programmatic `<a download>` click — same
   * reasoning as the private `/images/file/{id}` route. Filename is
   * "{invoice_number}.pdf".
   */
  async downloadInvoicePdf(invoiceId: number, invoiceNumber: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/admin/invoices/${invoiceId}/pdf`, {
      credentials: 'include',
    })

    if (!response.ok) {
      let message = response.statusText
      try {
        const body = (await response.json()) as { message?: string; error?: string }
        message = body.message ?? body.error ?? message
      } catch {
        // Body was not JSON (expected — errors are JSON, success is PDF bytes)
      }
      throw new GlorifyApiError(response.status, message)
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${invoiceNumber}.pdf`
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }
}

export const glorifyApiClient = new GlorifyApiClient()
